// Renderer, scene, sky, light, camera rig. The rig is the player's feet: in VR the headset moves the
// camera inside it, on a desk the OrbitControls do. Keeps `rig`, `camera` and `renderer.xr.enabled`;
// adds the sky dome, the palette switch (`applyTheme`) and the sun placement the course needs.

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { Hole } from '@shared/course';
import { themeFor, type Palette } from './theme';

export interface SkyUniforms {
  uTop: { value: THREE.Color };
  uMid: { value: THREE.Color };
  uHorizon: { value: THREE.Color };
  uSunDir: { value: THREE.Vector3 };
  uSunCol: { value: THREE.Color };
}

export interface World {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  rig: THREE.Group;
  controls: OrbitControls;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  fill: THREE.DirectionalLight;
  sky: THREE.Mesh;
  skyUniforms: SkyUniforms;
  /** the live palette; read it for accents (flag, HUD, aim line) */
  theme: Palette;
  /** unit vector from the hole toward the sun */
  sunDir: THREE.Vector3;
  applyTheme(name: string | undefined): Palette;
  /** aim the sun and fit its shadow camera to this hole */
  placeSun(hole: Hole): void;
  /** time of day, without changing the palette's hue */
  setSunElevation(deg: number, hole?: Hole): void;
  resize(width: number, height: number): void;
}

const col = (hex: string) => new THREE.Color(hex);
const dirFrom = (elDeg: number, azDeg: number) => {
  const el = (elDeg * Math.PI) / 180, az = (azDeg * Math.PI) / 180;
  return new THREE.Vector3(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el));
};

export function createWorld(container: HTMLElement): World {
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // never 0: a hidden document reports no size, and a 0x0 canvas draws the hole into nothing
  renderer.setSize(
    Math.max(1, container.clientWidth || window.innerWidth || 1280),
    Math.max(1, container.clientHeight || window.innerHeight || 720),
  );
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // Neutral holds the turf's hue where ACES turns it grey; the palettes are authored against it.
  const neutral = (THREE as unknown as { NeutralToneMapping?: THREE.ToneMapping }).NeutralToneMapping;
  renderer.toneMapping = neutral ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.xr.enabled = true;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(55, 16 / 9, 0.05, 4000);
  const rig = new THREE.Group();
  rig.name = 'rig';
  rig.add(camera);
  scene.add(rig);

  const hemi = new THREE.HemisphereLight(0xcfe0f5, 0x4a5a34, 1.0);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffe7c2, 2.5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 520;
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.02;
  scene.add(sun);
  scene.add(sun.target);
  // a cool bounce from the opposite side, so shadowed turf is not black
  const fill = new THREE.DirectionalLight(0xbcd3e4, 0.35);
  fill.position.set(70, 40, 90);
  scene.add(fill);

  // Sky: a three-stop gradient, a haze band at the horizon, and a sun disc with a wide falloff —
  // cheaper and more controllable than a physical sky, and it matches the fog by construction.
  const skyUniforms: SkyUniforms = {
    uTop: { value: col('#7fa9d8') },
    uMid: { value: col('#bcd3e4') },
    uHorizon: { value: col('#e7dcc4') },
    uSunDir: { value: new THREE.Vector3(0, 0.3, -1) },
    uSunCol: { value: col('#ffe7c2') },
  };
  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(2200, 32, 24),
    new THREE.ShaderMaterial({
      side: THREE.BackSide, depthWrite: false, fog: false, uniforms: skyUniforms as unknown as Record<string, THREE.IUniform>,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        varying vec3 vDir; uniform vec3 uTop, uMid, uHorizon, uSunCol; uniform vec3 uSunDir;
        void main(){
          float h = clamp(vDir.y, -1.0, 1.0);
          vec3 c = mix(uHorizon, uMid, smoothstep(-0.02, 0.30, h));
          c = mix(c, uTop, smoothstep(0.18, 0.85, h));
          float band = exp(-abs(h) * 14.0);
          c = mix(c, uHorizon, band * 0.65);
          float d = max(dot(normalize(vDir), normalize(uSunDir)), 0.0);
          c += uSunCol * (pow(d, 900.0) * 1.4 + pow(d, 22.0) * 0.22 + pow(d, 5.0) * 0.06);
          c = mix(c, uHorizon * 0.93, smoothstep(-0.05, -0.45, h));
          gl_FragColor = vec4(c, 1.0);
        }`,
    }),
  );
  sky.name = 'sky';
  sky.frustumCulled = false;
  scene.add(sky);

  const fog = new THREE.Fog(0xcfd8d2, 140, 640);
  scene.fog = fog;

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.maxPolarAngle = Math.PI * 0.499;
  controls.minDistance = 2;
  controls.maxDistance = 160;

  const world: World = {
    renderer, scene, camera, rig, controls, sun, hemi, fill, sky, skyUniforms,
    theme: themeFor('parkland'),
    sunDir: dirFrom(24, -38),

    applyTheme(name) {
      const p = themeFor(name);
      world.theme = p;
      skyUniforms.uTop.value.set(p.sky[0]);
      skyUniforms.uMid.value.set(p.sky[1]);
      skyUniforms.uHorizon.value.set(p.sky[2]);
      skyUniforms.uSunCol.value.set(p.sun);
      hemi.color.set(p.hemiSky);
      hemi.groundColor.set(p.hemiGround);
      sun.color.set(p.sun);
      sun.intensity = p.sunI;
      renderer.toneMappingExposure = p.exposure;
      fog.color.set(p.fog);
      fog.near = p.fogNear;
      fog.far = p.fogFar;
      world.sunDir = dirFrom(p.sunEl, p.sunAz);
      skyUniforms.uSunDir.value.copy(world.sunDir);
      return p;
    },

    placeSun(hole) {
      const target = new THREE.Vector3(hole.cup.x * 0.5, 0, hole.lengthM * 0.55);
      sun.position.copy(target).addScaledVector(world.sunDir, 300);
      sun.target.position.copy(target);
      sun.target.updateMatrixWorld();
      // Fit the shadow frustum to the hole: any tighter and the far trees lose their shadows, any
      // wider and the 2048 map goes soft and shows its edge on the fairway.
      const half = Math.max(hole.lengthM, hole.widthM) * 0.62;
      const cam = sun.shadow.camera;
      cam.left = -half; cam.right = half; cam.top = half; cam.bottom = -half;
      cam.updateProjectionMatrix();
    },

    setSunElevation(deg, hole) {
      world.sunDir = dirFrom(deg, world.theme.sunAz);
      skyUniforms.uSunDir.value.copy(world.sunDir);
      if (hole) world.placeSun(hole);
    },

    resize(width, height) {
      // updateStyle stays on: the canvas' CSS size has to follow the container, or a window
      // resize leaves the client drawing at its boot size
      const w = Math.max(1, width), h = Math.max(1, height);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    },
  };
  world.applyTheme('parkland');

  if (typeof ResizeObserver === 'function') {
    const ro = new ResizeObserver(() => world.resize(container.clientWidth || 1, container.clientHeight || 1));
    ro.observe(container);
  } else {
    window.addEventListener('resize', () => world.resize(window.innerWidth, window.innerHeight));
  }
  return world;
}
